<template>
  <div class="relative flex w-full">
    <div
      class="gradient-transition-effect group z-0 flex min-h-[7.875rem] w-full bg-gradient-to-tr from-blue-50 to-blue-10/40 p-6 after:bg-gradient-to-t after:from-blue-100/50 after:to-blue-50/50 sm:py-7 sm:pl-7"
    >
      <div class="flex w-full flex-col gap-3 sm:flex-row sm:gap-7">
        <!-- DATE -->
        <div class="min-w-[6.25rem]">
          <div
            class="mb-1 flex flex-wrap text-lg font-medium leading-7 sm:mb-2"
          >
            <time
              :datetime="data.event_date_from"
              :class="{ 'hidden sm:inline': isSameDay }"
            >
              {{ formatDate(data.event_date_from) }}
            </time>
            <time
              v-if="isSameDay"
              class="sm:hidden"
              :datetime="data.event_date_from"
            >
              {{ formatDateFull(data.event_date_from) }}
            </time>
            <template v-if="!isSameDay">
              <span class="mx-1">-</span>
              <time class="hidden sm:inline" :datetime="data.date">
                {{ formatDate(data.date) }}
              </time>
              <time class="sm:hidden" :datetime="data.date">
                {{ formatDateFull(data.date) }}
              </time>
            </template>
          </div>
          <p class="hidden text-sm opacity-60 sm:inline">
            {{ formatYear(data.event_date_from) }}
          </p>
        </div>

        <!-- TEXT -->
        <div class="mb-1 w-full 3xl:flex-1">
          <p
            class="mb-1 max-w-title text-lg font-medium leading-7 sm:mb-2"
            v-html="$replaceHtmlTags(data.heading)"
          />
          <p class="max-w-text text-base">
            {{ data.description }}
            <span v-if="data.description && data.event_location">
              &nbsp;|&nbsp;
            </span>
            {{ data.event_location }}
          </p>
        </div>

        <div class="flex min-h-[2.5rem] flex-col items-end justify-end sm:w-12">
          <!-- LINK -->
          <NuxtLink
            v-if="data.link_external || data.url"
            class="inline-block pl-2 text-blue-800 after:absolute after:inset-0"
            :aria-label="`${$t('accessibility.link_to')} Event: ${data.title}`"
            show-link-arrow
            :target="$useTarget(data.link_external || data.url)"
            :to="data.link_external || data.url"
          >
            <ExternalLinkIcon
              v-if="$isExternalUrl(data.link_external || data.url)"
              class="mb-1 w-5 sm:-translate-x-2"
            />
            <!-- Icon will be replaced with a arrow icon with growing transform -->
            <ArrowGrowIcon
              v-if="!$isExternalUrl(data.link_external || data.url)"
              class="w-6"
            />
          </NuxtLink>
        </div>
      </div>
    </div>
    <!-- TO CALENDAR -->
    <div
      class="absolute bottom-4 left-4 sm:bottom-auto sm:left-auto sm:right-2 sm:top-2"
    >
      <IcsButton
        :title="$removeHtmlTags(data.heading)"
        :start="data.event_date_from"
        :end="data.date"
        :location="data.event_location"
        :description="data.description"
        :url="data.link_external || data.url"
        :aria-label="$t('accessibility.save_event_to_calendar')"
        class="flex h-10 w-10 items-center justify-center rounded-sm bg-white transition-colors hover:bg-blue-200 sm:h-12 sm:w-12"
      >
        <div>
          <CalendarIcon
            class="h-5 w-5 text-blue-800 transition-colors hover:text-blue-dark sm:h-6 sm:w-6"
          />
          <span
            class="absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap text-sm text-blue-800 sm:hidden"
          >
            {{ $t('save_to_calendar') }}
          </span>
        </div>
      </IcsButton>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, required: true },
})

// COMPUTED
const isSameDay = computed(() => {
  let start = new Date(props.data.event_date_from)
  let end = new Date(props.data.date)

  return (
    start.getDate() === end.getDate() &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  )
})

// FUNCTIONS
function formatDate(date) {
  if (!date) {
    return ''
  }
  return new Date(date).toLocaleDateString('de', {
    day: '2-digit',
    month: '2-digit',
  })
}
function formatDateFull(date) {
  if (!date) {
    return ''
  }
  return new Date(date).toLocaleDateString('de', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatYear(date) {
  if (!date) {
    return ''
  }
  return new Date(date).toLocaleDateString('de', {
    year: 'numeric',
  })
}
</script>
